import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, X, Users, GraduationCap, Loader2, RefreshCw, Search, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import { classesApi, teachersApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'

const SPECIAL_CLASS_NAMES = ['Nursery', 'LKG', 'UKG']
const CLASS_NUMBERS = [...SPECIAL_CLASS_NAMES, ...Array.from({ length: 12 }, (_, i) => String(i + 1))]

const formatClassName = (classNumber) => {
  if (!classNumber) return ''
  return SPECIAL_CLASS_NAMES.includes(classNumber) ? classNumber : `CLASS ${classNumber}`
}

const getClassNumberFromName = (name = '') => {
  const trimmed = name.trim()
  const special = SPECIAL_CLASS_NAMES.find((s) => s.toLowerCase() === trimmed.toLowerCase())
  if (special) return special
  const normalized = trimmed.replace(/^CLASS\s+/i, '').trim()
  return CLASS_NUMBERS.find((n) => n.toLowerCase() === normalized.toLowerCase()) || ''
}

const emptyForm = { classNumber: '', section: '' }

const getUserName = (u) => {
  if (!u) return '—'
  return u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—'
}
const getUserId = (u) => u?._id || u?.id || u

const gradeColors = {
  '1': 'bg-pink-50 text-pink-700', '2': 'bg-rose-50 text-rose-700',
  '3': 'bg-orange-50 text-orange-700', '4': 'bg-amber-50 text-amber-700',
  '5': 'bg-yellow-50 text-yellow-700', '6': 'bg-pink-50 text-pink-700',
  '7': 'bg-purple-50 text-purple-700', '8': 'bg-blue-50 text-blue-700',
  '9': 'bg-green-50 text-green-700', '10': 'bg-orange-50 text-orange-700',
  '11': 'bg-red-50 text-red-700', '12': 'bg-indigo-50 text-indigo-700',
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[...Array(4)].map((_, i) => (
        <td key={i} className="table-td"><div className="h-3 bg-gray-100 rounded w-3/4" /></td>
      ))}
    </tr>
  )
}

export default function ClassesPage() {
  const navigate = useNavigate()
  const [classes, setClasses] = useState([])
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [deleteId, setDeleteId] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [teacherModalClass, setTeacherModalClass] = useState(null)
  const [selectedTeacherId, setSelectedTeacherId] = useState('')
  const [teacherSearch, setTeacherSearch] = useState('')
  const [assigningTeacher, setAssigningTeacher] = useState(false)
  const [showUnavailableTeachers, setShowUnavailableTeachers] = useState(false)
  const [teacherDropdownOpen, setTeacherDropdownOpen] = useState(false)

  const fetchClasses = async () => {
    try {
      setLoading(true)
      const [classData, teacherData] = await Promise.all([
        classesApi.getAll(),
        teachersApi.getAll(),
      ])
      setClasses(Array.isArray(classData) ? classData : classData?.classes ?? [])
      setTeachers(Array.isArray(teacherData) ? teacherData : teacherData?.users ?? teacherData?.teachers ?? [])
    } catch {
      toast.error('Failed to load classes')
      setClasses([])
      setTeachers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchClasses() }, [])

  const openAdd = () => { setEditing(null); setForm(emptyForm); setModalOpen(true) }

  const openEdit = (c) => {
    setEditing(c._id || c.id)
    setForm({
      classNumber: getClassNumberFromName(c.name),
      section: (c.section || '').toUpperCase(),
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.classNumber) { toast.error('Select a class number'); return }
    const name = formatClassName(form.classNumber)
    const section = form.section.trim().toUpperCase()
    const payload = { name, ...(section ? { section } : { section: '' }) }
    try {
      setSaving(true)
      if (editing) {
        const updated = await classesApi.update(editing, payload)
        setClasses((prev) => prev.map((c) => (c._id || c.id) === editing ? { ...c, ...updated } : c))
        toast.success('Class updated')
      } else {
        const created = await classesApi.create(payload)
        setClasses((prev) => [...prev, created])
        toast.success('Class created')
      }
      setModalOpen(false)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save class')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true)
      await classesApi.remove(deleteId)
      setClasses((prev) => prev.filter((c) => (c._id || c.id) !== deleteId))
      toast.success('Class removed')
      setDeleteId(null)
    } catch {
      toast.error('Failed to remove class')
    } finally {
      setDeleting(false)
    }
  }

  const totalStudents = classes.reduce((sum, c) => {
    const count = c.studentCount ?? (typeof c.students === 'number' ? c.students : 0)
    return sum + (Number(count) || 0)
  }, 0)

  const gradeSet = [...new Set(classes.map((c) => c.grade).filter(Boolean))]

  const sortedClasses = useMemo(() => [...classes].sort((a, b) => {
    const aIdx = CLASS_NUMBERS.indexOf(getClassNumberFromName(a.name))
    const bIdx = CLASS_NUMBERS.indexOf(getClassNumberFromName(b.name))
    if (aIdx !== bIdx) return aIdx - bIdx
    return (a.section || '').localeCompare(b.section || '')
  }), [classes])

  const teacherAssignments = useMemo(() => {
    const assignments = new Map()
    classes.forEach((classItem) => {
      const teacherId = getUserId(classItem.classTeacher)
      if (!teacherId) return
      assignments.set(String(teacherId), `${classItem.name}${classItem.section ? ` — ${classItem.section}` : ''}`)
    })
    return assignments
  }, [classes])

  const filteredTeachers = useMemo(() => {
    const query = teacherSearch.trim().toLowerCase()
    const list = teachers.filter((teacher) => {
      if (!query) return true
      return [getUserName(teacher), teacher.email, teacher.mobile]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    })

    return list.sort((a, b) => {
      const aSelected = String(getUserId(a)) === String(selectedTeacherId)
      const bSelected = String(getUserId(b)) === String(selectedTeacherId)
      if (aSelected !== bSelected) return aSelected ? -1 : 1
      return getUserName(a).localeCompare(getUserName(b))
    })
  }, [selectedTeacherId, teacherSearch, teachers])

  const openTeacherModal = (classItem) => {
    setTeacherModalClass(classItem)
    setSelectedTeacherId(getUserId(classItem.classTeacher) || '')
    setTeacherSearch('')
    setShowUnavailableTeachers(false)
    setTeacherDropdownOpen(false)
  }

  const handleAssignClassTeacher = async () => {
    if (!teacherModalClass || !selectedTeacherId) {
      toast.error('Select a class teacher first')
      return
    }

    try {
      setAssigningTeacher(true)
      const updated = await classesApi.assignClassTeacher(teacherModalClass._id || teacherModalClass.id, { teacherId: selectedTeacherId })
      setClasses((prev) =>
        prev.map((classItem) =>
          (classItem._id || classItem.id) === (teacherModalClass._id || teacherModalClass.id)
            ? { ...classItem, ...updated }
            : classItem
        )
      )
      toast.success('Class teacher updated')
      setTeacherModalClass(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign class teacher')
    } finally {
      setAssigningTeacher(false)
    }
  }

  const teacherModalClassLabel = teacherModalClass
    ? `${teacherModalClass.name}${teacherModalClass.section ? ` — ${teacherModalClass.section}` : ''}`
    : ''

  const { availableTeacherOptions, unavailableTeacherOptions } = useMemo(() => {
    if (!teacherModalClass) return { availableTeacherOptions: [], unavailableTeacherOptions: [] }

    const available = []
    const unavailable = []

    filteredTeachers.forEach((teacher) => {
      const teacherId = getUserId(teacher)
      const assignedClass = teacherAssignments.get(String(teacherId))
      const isTakenElsewhere = assignedClass && assignedClass !== teacherModalClassLabel

      if (isTakenElsewhere) {
        unavailable.push({ teacher, assignedClass })
      } else {
        available.push({ teacher, assignedClass })
      }
    })

    return {
      availableTeacherOptions: available,
      unavailableTeacherOptions: unavailable,
    }
  }, [filteredTeachers, teacherAssignments, teacherModalClass, teacherModalClassLabel])

  const selectedTeacher = useMemo(
    () => teachers.find((teacher) => String(getUserId(teacher)) === String(selectedTeacherId)) || null,
    [selectedTeacherId, teachers]
  )

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Classes</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? '...' : `${classes.length} classes · ${totalStudents} students total`}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button onClick={fetchClasses} className="btn-secondary p-2" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={openAdd} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Class
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Classes', value: classes.length, color: 'text-blue-600' },
            { label: 'Total Students', value: totalStudents, color: 'text-green-600' },
            { label: 'Avg Class Size', value: classes.length ? Math.round(totalStudents / classes.length) : 0, color: 'text-purple-600' },
            { label: 'Grade Levels', value: gradeSet.length, color: 'text-orange-600' },
          ].map((s) => (
            <div key={s.label} className="card p-4 text-center">
              <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-th">Class</th>
                <th className="table-th">Class Teacher</th>
                <th className="table-th hidden sm:table-cell">Students</th>
                <th className="table-th">Actions</th>
                <th className="table-th hidden sm:table-cell"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
              ) : classes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-gray-400 text-sm">
                    No classes found
                  </td>
                </tr>
              ) : sortedClasses.map((c) => {
                const classLabel = c.name + (c.section ? ` — ${c.section}` : '')
                const teacherName = getUserName(
                  typeof c.classTeacher === 'object' ? c.classTeacher : null
                )
                const studentCount = c.studentCount ?? (
                  typeof c.students === 'number' ? c.students : (
                    Array.isArray(c.students) ? c.students.length : '—'
                  )
                )
                return (
                  <tr key={c._id || c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="table-td">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${gradeColors[c.grade] || 'bg-gray-100 text-gray-700'}`}>
                        {classLabel}
                      </span>
                    </td>
                    <td className="table-td">
                      <div className="flex items-center justify-between gap-3 min-w-[290px]">
                        <div className="flex items-center gap-3 min-w-0">
                          {typeof c.classTeacher === 'object' && getUserId(c.classTeacher) ? (
                            <Avatar src={c.classTeacher.avatar} name={teacherName} size="md" />
                          ) : (
                            <div className="w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0 bg-gray-100 text-gray-400">
                              <GraduationCap className="w-4 h-4" />
                            </div>
                          )}
                          <div className="min-w-0">
                            {typeof c.classTeacher === 'object' && getUserId(c.classTeacher) ? (
                              <>
                                <Link to={`/users/${getUserId(c.classTeacher)}`} className="text-gray-900 font-semibold hover:text-primary-700 transition-colors block truncate">
                                  {teacherName}
                                </Link>
                                <p className="text-xs text-gray-400 mt-0.5">Lead teacher assigned</p>
                              </>
                            ) : (
                              <>
                                <p className="text-gray-500 font-semibold">No class teacher</p>
                                <p className="text-xs text-gray-400 mt-0.5">Assign one lead teacher for this section</p>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => openTeacherModal(c)}
                          className={`inline-flex items-center justify-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors flex-shrink-0 ${
                            getUserId(c.classTeacher)
                              ? 'border-primary-200 text-primary-700 hover:border-primary-300 hover:bg-primary-50'
                              : 'border-gray-200 text-gray-700 hover:border-primary-200 hover:text-primary-700 hover:bg-primary-50'
                          }`}
                        >
                          {getUserId(c.classTeacher) ? 'Change' : 'Assign'}
                        </button>
                      </div>
                    </td>
                    <td className="table-td hidden sm:table-cell">
                      <div className="flex items-center gap-1 text-gray-500">
                        <Users className="w-3.5 h-3.5" /> {studentCount}
                      </div>
                    </td>
                    <td className="table-td">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(c)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteId(c._id || c.id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="table-td hidden sm:table-cell">
                      <button
                        onClick={() => navigate(`/classes/${c._id || c.id}`)}
                        className="flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700 border border-primary-200 hover:bg-primary-50 px-2.5 py-1.5 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" /> View
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900">{editing ? 'Edit Class' : 'Add Class'}</h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Class number chips */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-3">Class *</label>
                <div className="flex flex-wrap gap-2">
                  {CLASS_NUMBERS.map((num) => {
                    const active = form.classNumber === num
                    return (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, classNumber: num }))}
                        className={`min-w-[48px] h-11 px-3 rounded-xl border-2 text-sm font-bold transition-all ${
                          active
                            ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:text-primary-700'
                        }`}
                      >
                        {num}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Section */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Section <span className="font-normal text-gray-400">(optional — e.g. A, B, C)</span>
                </label>
                <input
                  className="input-field w-full"
                  value={form.section}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      section: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 3),
                    }))
                  }
                  placeholder="A"
                  maxLength={3}
                />
              </div>

              {/* Live preview */}
              {form.classNumber && (
                <div className="rounded-xl border-2 border-primary-200 bg-primary-50 px-4 py-3 text-center">
                  <p className="text-xs text-primary-500 font-medium mb-1">Preview</p>
                  <p className="text-lg font-bold text-primary-700">
                    {formatClassName(form.classNumber)}
                    {form.section.trim() ? ` — ${form.section.trim()}` : ''}
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 pb-6 flex gap-3 justify-end border-t border-gray-100 pt-4">
              <button onClick={() => setModalOpen(false)} className="btn-secondary" disabled={saving}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'Save Changes' : 'Create Class'}
              </button>
            </div>
          </div>
        </div>
      )}

      {teacherModalClass && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-bold text-gray-900">Assign Class Teacher</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {teacherModalClassLabel} · choose one lead teacher for this class section.
                </p>
              </div>
              <button onClick={() => setTeacherModalClass(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 grid lg:grid-cols-[minmax(0,1.15fr)_320px] gap-6">
              <div className="space-y-5">
                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">Teacher Picker</h4>
                      <p className="text-xs text-gray-500 mt-1">Use the dropdown to search and pick any available teacher without stretching the modal.</p>
                    </div>
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      {availableTeacherOptions.length} available
                    </span>
                  </div>

                  <div className="relative mt-4">
                    <button
                      type="button"
                      onClick={() => setTeacherDropdownOpen((prev) => !prev)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                        teacherDropdownOpen
                          ? 'border-primary-300 bg-primary-50/60'
                          : 'border-gray-200 hover:border-primary-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {selectedTeacher ? (
                          <Avatar src={selectedTeacher.avatar} name={getUserName(selectedTeacher)} size="sm" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
                            <Search className="w-4 h-4" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
                            {selectedTeacher ? 'Selected teacher' : 'Choose teacher'}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 truncate mt-1">
                            {selectedTeacher ? getUserName(selectedTeacher) : 'Search by name, email, or mobile'}
                          </p>
                          <p className="text-xs text-gray-500 truncate mt-1">
                            {selectedTeacher
                              ? (selectedTeacher.email || selectedTeacher.mobile || 'No contact info')
                              : 'Click to open the teacher dropdown'}
                          </p>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${teacherDropdownOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {teacherDropdownOpen && (
                      <div className="absolute left-0 right-0 top-full z-20 mt-2 rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
                        <div className="border-b border-gray-100 p-3">
                          <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <input
                              value={teacherSearch}
                              onChange={(e) => setTeacherSearch(e.target.value)}
                              placeholder="Type teacher name, email, or mobile..."
                              className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {teacherSearch.trim()
                              ? `${availableTeacherOptions.length} match${availableTeacherOptions.length === 1 ? '' : 'es'} found`
                              : 'All assignable teachers are listed here'}
                          </p>
                        </div>

                        <div className="max-h-72 overflow-y-auto p-2">
                          {availableTeacherOptions.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-8">No available teachers match your search.</p>
                          ) : availableTeacherOptions.map(({ teacher }) => {
                            const teacherId = getUserId(teacher)
                            const isSelected = String(selectedTeacherId) === String(teacherId)
                            return (
                              <button
                                key={teacherId}
                                type="button"
                                onClick={() => {
                                  setSelectedTeacherId(String(teacherId))
                                  setTeacherDropdownOpen(false)
                                }}
                                className={`w-full rounded-xl px-3 py-2.5 text-left transition-colors ${
                                  isSelected ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <Avatar src={teacher.avatar} name={getUserName(teacher)} size="sm" />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-900 truncate">{getUserName(teacher)}</p>
                                    <p className="text-xs text-gray-500 truncate mt-0.5">{teacher.email || teacher.mobile || 'No contact info'}</p>
                                  </div>
                                  {isSelected && <span className="rounded-full bg-primary-600 px-2.5 py-1 text-[11px] font-semibold text-white">Selected</span>}
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 mt-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Quick guidance</p>
                    <p className="text-sm text-gray-600 mt-2">
                      Search once, pick one teacher from the dropdown, then save the class teacher assignment on the right.
                    </p>
                  </div>
                </div>

                {unavailableTeacherOptions.length > 0 && (
                  <div className="rounded-2xl border border-amber-100 bg-amber-50/60 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowUnavailableTeachers((prev) => !prev)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                    >
                      <div>
                        <p className="text-sm font-bold text-amber-900">Teachers Already Leading Another Class</p>
                        <p className="text-xs text-amber-700 mt-1">{unavailableTeacherOptions.length} teachers are blocked because they already lead another class section.</p>
                      </div>
                      {showUnavailableTeachers ? <ChevronUp className="w-4 h-4 text-amber-700" /> : <ChevronDown className="w-4 h-4 text-amber-700" />}
                    </button>

                    {showUnavailableTeachers && (
                      <div className="border-t border-amber-100 bg-white/80 px-4 py-3 max-h-48 overflow-y-auto space-y-2">
                        {unavailableTeacherOptions.map(({ teacher, assignedClass }) => (
                          <div key={getUserId(teacher)} className="rounded-xl border border-gray-100 bg-white px-3 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar src={teacher.avatar} name={getUserName(teacher)} size="sm" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-700 truncate">{getUserName(teacher)}</p>
                                <p className="text-xs text-gray-500 mt-1 truncate">{teacher.email || teacher.mobile || 'No contact info'}</p>
                                <p className="text-xs text-amber-600 mt-2">Already class teacher of {assignedClass}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">Current Class Teacher</p>
                  {getUserId(teacherModalClass.classTeacher) ? (
                    <div className="flex items-center gap-3 mt-3">
                      <Avatar
                        src={teacherModalClass.classTeacher.avatar}
                        name={getUserName(teacherModalClass.classTeacher)}
                        size="md"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-blue-900 truncate">{getUserName(teacherModalClass.classTeacher)}</p>
                        <p className="text-xs text-blue-700 truncate mt-1">
                          {teacherModalClass.classTeacher.email || teacherModalClass.classTeacher.mobile || 'No contact info'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 mt-3">
                      <div className="w-9 h-9 rounded-full bg-white text-blue-600 flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-blue-900">No class teacher assigned yet</p>
                        <p className="text-xs text-blue-700 mt-1">Pick one teacher from the dropdown.</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-primary-100 bg-white shadow-sm px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Selected Teacher</p>
                  {selectedTeacher ? (
                    <div className="flex items-center gap-3 mt-3">
                      <Avatar src={selectedTeacher.avatar} name={getUserName(selectedTeacher)} size="lg" />
                      <div className="min-w-0">
                        <p className="text-base font-bold text-gray-900 truncate">{getUserName(selectedTeacher)}</p>
                        <p className="text-sm text-gray-500 mt-1 truncate">{selectedTeacher.email || selectedTeacher.mobile || 'No contact info'}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mt-3">No teacher selected yet.</p>
                  )}
                  {selectedTeacher && (
                    <>
                      <Link to={`/users/${getUserId(selectedTeacher)}`} className="inline-flex text-xs font-semibold text-primary-700 hover:text-primary-800 mt-3">
                        Open teacher profile
                      </Link>
                    </>
                  )}
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4">
                  <p className="text-sm font-semibold text-gray-900">What changed</p>
                  <ul className="text-xs text-gray-500 mt-3 space-y-2">
                    <li>The teacher list now lives inside a compact dropdown instead of a long card wall.</li>
                    <li>Every visible teacher now has an avatar for faster scanning.</li>
                    <li>Blocked class teachers stay tucked away in a separate expandable section.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 flex items-center justify-end gap-3">
              <button onClick={() => setTeacherModalClass(null)} className="btn-secondary" disabled={assigningTeacher}>Cancel</button>
              <button onClick={handleAssignClassTeacher} className="btn-primary flex items-center gap-2" disabled={assigningTeacher || !selectedTeacherId}>
                {assigningTeacher && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Class Teacher
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Remove Class?</h3>
            <p className="text-sm text-gray-500 mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="btn-secondary flex-1" disabled={deleting}>
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />} Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
